import { useState } from "react";
import Pricing from "../../data/Pricing";
import { trackPostHogEvent, registerPostHogProperty } from "../../utils/posthogUtils";
import styles from "./ProductCard.module.css";
import sublingualCardImage from "../../assets/solo-bh-ad-300x250.webp";
import injectableCardImage from "../../assets/solo-bh-ad-injectable.webp";
import { useNavigate } from "react-router-dom";

const TINT_CLASSNAME = {
    amber: 'tAmber',
    cyan: 'tCyan',
    leaf: 'tLeaf',
    teal: 'tTeal',
    rose: 'tRose',
};

const MEDICATION_IMAGE = {
    'Semaglutide-suspension-threeMonths': sublingualCardImage,
    'Semaglutide-injectable-threeMonths': injectableCardImage,
    'Tirzepatide-suspension-threeMonths': sublingualCardImage,
    'Tirzepatide-injectable-threeMonths': injectableCardImage,
    'Semaglutide-suspension-monthly': sublingualCardImage,
    'Semaglutide-injectable-monthly': injectableCardImage,
    'Tirzepatide-suspension-monthly': sublingualCardImage,
    'Tirzepatide-injectable-monthly': injectableCardImage,

};

const WI_TABLE_ROWS = [
    ['Clinical Evaluation', 'Independent licensed provider'],
    ['Prescribing', 'When medically appropriate'],
    ['Dosage', 'All included, no price change'],
    ['Care Coordinator', 'Dedicated patient support'],
    ['Follow-Ups', 'Ongoing clinical check-ins'],
    ['Pharmacy', 'Licensed pharmacy fulfillment'],
    ['Renewals', 'Automatic plan renewal'],
    ['Guarantee', 'Money back if not approved'],
];

function CheckIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
    );
}

const SOCIAL_PROOF_ICONS = {
    trend: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4BB96C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>,
    star: <svg width="14" height="14" viewBox="0 0 24 24" fill="#F5A623" stroke="#F5A623" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>,
    hot: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E8603C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path></svg>,
    tag: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4BB96C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>,
};

const FORM_LABEL = { suspension: 'Sublingual', injectable: 'Injectable' };

const FORM_CHIP_LABEL = { suspension: 'Oral Drops', injectable: 'Injectable' };

const FORM_ICON = {
    suspension: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.5c3.5 4 6 7 6 10.5a6 6 0 0 1-12 0c0-3.5 2.5-6.5 6-10.5z"></path></svg>,
    injectable: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 2 4 4"></path><path d="m17 7 3-3"></path><path d="M19 9 8.7 19.3c-.94.94-2.46.94-3.4 0l-.6-.6c-.94-.94-.94-2.46 0-3.4L15 5"></path><path d="m9 11 4 4"></path><path d="m5 19-3 3"></path><path d="m14 4 6 6"></path></svg>,
};

export default function ProductCard({ medication, dosageForm, frequency, tint, perks, topBadge, membersChoice, socialProof, isSale }) {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const pricing = Pricing();
    const isWholeSale = pricing.isWholesale();
    const pricingOptions = pricing.getPricingOptionsByProductName(medication.toLowerCase());
    const retailPricingOptions = pricing.getRetailPrices()[medication.toLowerCase()];

    const currentPricingDetails = pricingOptions?.[dosageForm]?.[frequency] ?? null;
    const retailPricingDetails = isWholeSale ? (retailPricingOptions?.[dosageForm]?.[frequency] ?? null) : null;
    const hasDiscount = Boolean(isWholeSale && retailPricingDetails && currentPricingDetails && retailPricingDetails.total !== currentPricingDetails.total);

    const handlePlanSelection = () => {
        if (!currentPricingDetails?.id) return;
        localStorage.setItem('plan_id', currentPricingDetails.id);
        registerPostHogProperty('initially_selected_frequency', frequency);
        trackPostHogEvent('plan_selected', {
            product: medication.toLowerCase(),
            dosage_form: dosageForm,
            frequency,
            plan_id: currentPricingDetails.id
        });
        navigate('/checkout/payment');
    };

    return (
        <article className={`${styles.card} ${isSale ? styles.cardSale : ''}`} data-carousel-item>
            <div className={`${styles.media} ${styles[TINT_CLASSNAME[tint]]}`}>
                <div className={styles.badgeRow}>
                    {membersChoice && <span className={styles.membersBadge}><span className={styles.pulseDot}></span>Members Choice</span>}
                    {topBadge && <span className={styles.topBadge}>{topBadge}</span>}
                    <span className={styles.formChip}>{FORM_ICON[dosageForm]}{FORM_CHIP_LABEL[dosageForm]}</span>
                </div>
                <img src={MEDICATION_IMAGE[`${medication}-${dosageForm}-${frequency}`]} alt={`${medication} ${FORM_LABEL[dosageForm]}`} />
            </div>

            <div className={styles.body}>
                <div>
                    <h3 className={styles.title}>{medication} {FORM_LABEL[dosageForm]}</h3>
                    <div className={`${styles.socialProof} ${!socialProof ? styles.socialProofHidden : ''}`}>
                        {socialProof ? <>{SOCIAL_PROOF_ICONS[socialProof.icon]} {socialProof.label}</> : <>&nbsp;</>}
                    </div>
                </div>

                <div className={styles.priceBlock}>
                    {hasDiscount && <span className={styles.priceWas}>${retailPricingDetails.total.split('.')[0]}</span>}
                    <span className={styles.priceAmt}>${currentPricingDetails?.total?.split('.')[0] ?? '—'}</span>
                    <span className={styles.pricePer}>
                        {frequency === 'monthly' ? '/month' : `total · $${currentPricingDetails?.monthly_amount?.split('.')[0]}/mo`}
                    </span>
                </div>

                <ul className={styles.perks}>
                    {perks.map((perk) => (
                        <li key={perk}><CheckIcon /> {perk}</li>
                    ))}
                </ul>

                <button type="button" className={styles.wiToggle} onClick={() => setIsOpen((open) => !open)} aria-expanded={isOpen}>
                    <span>What's included?</span>
                    <span className={`${styles.wiChevron} ${isOpen ? styles.wiChevronOpen : ''}`}>&#9660;</span>
                </button>
                {isOpen && (
                    <div className={styles.wiPanel}>
                        <table className={styles.wiTable}>
                            <tbody>
                                {WI_TABLE_ROWS.map(([label, value]) => (
                                    <tr key={label}><td>{label}</td><td>{value}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <button type="button" className={`${styles.cta} ${isSale ? styles.ctaSale : ''}`} onClick={handlePlanSelection}>
                    Start My Program
                </button>
            </div>
        </article>
    );
}
